import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, SlidersHorizontal, Save, Plus, Trash2, Check } from "lucide-react";

const FLAG_META = [
  { key: "trail_map", label: "Trail Map", help: "Show the Trail Map screen (pure navigation)." },
  { key: "plain_words", label: "Plain Words layer", help: "Let readers turn on plain-language cards on the densest pages." },
  { key: "bring_to_session", label: "\u201cBring this to a session\u201d buttons", help: "Let readers tag journal notes to bring to a counselor." },
  { key: "social_circles_drafts", label: "Social Circles prompts", help: "Surface the published Social Circles reflection prompts to readers." },
];

export default function AdminBookExtras() {
  const [token] = useState(() => getAdminToken());
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/book-extras`, auth); setData(data); }
    catch (e) { if (e.response?.status === 401) adminLogout(); }
    finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/book-extras`, {
        flags: data.flags, trail_map_note: data.trail_map_note, social_waypoints: data.social_waypoints,
      }, auth);
      setSavedAt(Date.now());
    } catch (e) { if (e.response?.status === 401) adminLogout(); }
    finally { setSaving(false); }
  };

  const setFlag = (k, v) => setData((d) => ({ ...d, flags: { ...d.flags, [k]: v } }));
  const setNote = (v) => setData((d) => ({ ...d, trail_map_note: v }));
  const setWp = (i, patch) => setData((d) => ({ ...d, social_waypoints: d.social_waypoints.map((w, j) => j === i ? { ...w, ...patch } : w) }));
  const addWp = () => setData((d) => ({ ...d, social_waypoints: [...d.social_waypoints, { id: `sc${Date.now()}`, prompt: "", status: "draft" }] }));
  const delWp = (i) => setData((d) => ({ ...d, social_waypoints: d.social_waypoints.filter((_, j) => j !== i) }));

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-book-extras">
      <div className="mx-auto max-w-3xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="inline-flex items-center gap-2 font-display text-3xl text-ink">
              <SlidersHorizontal className="h-6 w-6 text-gold-deep" /> Book Extras
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-soft">
              Turn the new reader features on or off, write the Trail Map note, and manage the Social Circles
              draft prompts. Everything here is additive and reversible.
            </p>
          </div>
          <button onClick={save} disabled={saving || !data} data-testid="save-book-extras"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 transition hover:bg-ink/90 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            {savedAt && !saving ? <Check className="h-4 w-4 text-sage" /> : null}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-3xl space-y-6 px-5 py-6 sm:px-8">
        {loading || !data ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>
        ) : (
          <>
            <section className="rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/50">
              <h2 className="font-display text-lg text-ink">Feature toggles</h2>
              <div className="mt-3 space-y-3">
                {FLAG_META.map((f) => (
                  <label key={f.key} className="flex items-start justify-between gap-4" data-testid={`flag-${f.key}`}>
                    <span>
                      <span className="block font-display text-sm text-ink">{f.label}</span>
                      <span className="block text-xs text-ink-muted">{f.help}</span>
                    </span>
                    <button type="button" role="switch" aria-checked={!!data.flags[f.key]}
                      onClick={() => setFlag(f.key, !data.flags[f.key])} data-testid={`flag-toggle-${f.key}`}
                      className={`relative mt-0.5 h-6 w-11 flex-none rounded-full transition ${data.flags[f.key] ? "bg-gold-deep" : "bg-ivory-400"}`}>
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${data.flags[f.key] ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                  </label>
                ))}
              </div>
            </section>

            <section className="rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/50">
              <h2 className="font-display text-lg text-ink">Trail Map note — “Walk this with someone”</h2>
              <p className="mt-1 text-xs text-ink-muted">Shown at the top of the Trail Map. Write the final wording.</p>
              <textarea rows={4} value={data.trail_map_note} onChange={(e) => setNote(e.target.value)} data-testid="trail-map-note-input"
                className="mt-2 w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
            </section>

            <section className="rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/50">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg text-ink">Social Circles — draft prompts</h2>
                <button onClick={addWp} data-testid="add-social-waypoint" className="inline-flex items-center gap-1.5 rounded-full border border-ivory-400/70 px-3 py-1.5 text-xs font-display text-ink hover:border-gold/50"><Plus className="h-3.5 w-3.5" /> Add slot</button>
              </div>
              <p className="mt-1 text-xs text-ink-muted">Placeholders for you to write or approve. Set to “Published” to let them show to readers (needs the Social Circles toggle on).</p>
              <div className="mt-3 space-y-3">
                {(data.social_waypoints || []).map((w, i) => (
                  <div key={w.id} className="rounded-xl bg-[#FCFBF7] p-3 ring-1 ring-ivory-400/50" data-testid={`social-waypoint-${i}`}>
                    <textarea rows={2} value={w.prompt} onChange={(e) => setWp(i, { prompt: e.target.value })} data-testid={`social-prompt-${i}`}
                      placeholder="Reflection prompt…" className="w-full rounded-lg border border-ivory-400/70 bg-white p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                    <div className="mt-2 flex items-center gap-2">
                      {["draft", "published"].map((s) => (
                        <button key={s} onClick={() => setWp(i, { status: s })} data-testid={`social-status-${i}-${s}`}
                          className={`rounded-full border px-3 py-1 text-xs capitalize transition ${w.status === s ? "border-gold bg-gold/15 text-gold-deep" : "border-ivory-400/70 text-ink-soft hover:border-gold/40"}`}>{s}</button>
                      ))}
                      <button onClick={() => delWp(i)} data-testid={`social-delete-${i}`} className="ml-auto inline-flex items-center gap-1 text-xs text-terracotta-deep/80 hover:text-terracotta-deep"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                    </div>
                  </div>
                ))}
                {!data.social_waypoints?.length && <p className="text-sm text-ink-muted">No slots yet — add one above.</p>}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
